import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../components/Layout";
import "../../styles/lab/lab.css";
import authService from "../../services/auth/authService";
import {
  envoyerMessage,
  envoyerMessageGroupe,
  getDestinataires,
  getMessages,
  getNotifications,
  marquerNotificationLue,
  marquerToutesLues,
  supprimerMessage,
} from "../../services/lab/messagerieService";

const quand = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const jours = Math.floor((Date.now() - d) / 86400000);
  if (jours === 0) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (jours === 1) return "Hier";
  if (jours < 7) return `Il y a ${jours} jours`;
  return d.toLocaleDateString("fr-FR");
};

export default function Messagerie() {
  const [onglet, setOnglet] = useState("reception"); // reception | envoi | notifications
  const [messages, setMessages] = useState([]);
  const [nonLus, setNonLus] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [nonLues, setNonLues] = useState(0);
  const [ouvert, setOuvert] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [flash, setFlash] = useState(null);

  const [redaction, setRedaction] = useState(false);
  const [destinataires, setDestinataires] = useState([]);
  const [brouillon, setBrouillon] = useState({ destinataire: "", objet: "", contenu: "" });
  const [envoi, setEnvoi] = useState(false);

  const notifier = (msg, type = "succes") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      if (onglet === "notifications") {
        const d = await getNotifications();
        setNotifications(d.notifications);
        setNonLues(d.non_lues);
      } else {
        const d = await getMessages(onglet);
        setMessages(d.messages);
        setNonLus(d.non_lus);
      }
    } catch {
      notifier("Chargement impossible.", "erreur");
    } finally {
      setChargement(false);
    }
  }, [onglet]);

  useEffect(() => { charger(); }, [charger]);

  // Pastille du nombre de non-lus, indépendante de l'onglet affiché.
  useEffect(() => {
    getMessages("reception").then((d) => setNonLus(d.non_lus)).catch(() => {});
    getNotifications().then((d) => setNonLues(d.non_lues)).catch(() => {});
  }, []);

  const ouvrirRedaction = async () => {
    setRedaction(true);
    setBrouillon({ destinataire: "", objet: "", contenu: "" });
    try { setDestinataires(await getDestinataires()); }
    catch { notifier("Impossible de charger les destinataires.", "erreur"); }
  };

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      if (brouillon.destinataire.startsWith("groupe:")) {
        const { envoyes } = await envoyerMessageGroupe(
          brouillon.destinataire.slice("groupe:".length), brouillon.objet, brouillon.contenu);
        notifier(`Message envoyé à ${envoyes} personne${envoyes > 1 ? "s" : ""}.`);
      } else {
        await envoyerMessage(Number(brouillon.destinataire), brouillon.objet, brouillon.contenu);
        notifier("Message envoyé.");
      }
      setRedaction(false);
      if (onglet === "envoi") charger();
    } catch (err) {
      const d = err.response?.data;
      notifier(d?.groupe?.[0] || d?.destinataire?.[0] || d?.contenu?.[0] || "Envoi impossible.", "erreur");
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (id) => {
    try {
      await supprimerMessage(id);
      notifier("Message supprimé.");
      setOuvert(null);
      charger();
    } catch { notifier("Suppression impossible.", "erreur"); }
  };

  const toutLire = async () => {
    await marquerToutesLues();
    setNonLues(0);
    charger();
  };

  const lireNotification = async (n) => {
    if (!n.lue) {
      await marquerNotificationLue(n.id);
      setNonLues((x) => Math.max(0, x - 1));
      charger();
    }
  };

  return (
    <Layout>
      <div className="lab-page">
        <header className="lab-header">
          <div>
            <h1><i className="fa-solid fa-comments"></i> Messagerie</h1>
            <p className="lab-sous-titre">Messages internes et notifications</p>
          </div>
          <button className="btn-primaire" onClick={ouvrirRedaction}>
            <i className="fa-solid fa-pen"></i> Nouveau message
          </button>
        </header>

        {flash && <div className={`lab-flash ${flash.type}`}>{flash.msg}</div>}

        <div className="msg-onglets">
          <button className={onglet === "reception" ? "actif" : ""} onClick={() => { setOnglet("reception"); setOuvert(null); }}>
            <i className="fa-solid fa-inbox"></i> Réception
            {nonLus > 0 && <span className="msg-pastille">{nonLus}</span>}
          </button>
          <button className={onglet === "envoi" ? "actif" : ""} onClick={() => { setOnglet("envoi"); setOuvert(null); }}>
            <i className="fa-solid fa-paper-plane"></i> Envoyés
          </button>
          <button className={onglet === "notifications" ? "actif" : ""} onClick={() => { setOnglet("notifications"); setOuvert(null); }}>
            <i className="fa-solid fa-bell"></i> Notifications
            {nonLues > 0 && <span className="msg-pastille">{nonLues}</span>}
          </button>
          {onglet === "notifications" && nonLues > 0 && (
            <button className="btn-lien msg-tout-lire" onClick={toutLire}>Tout marquer comme lu</button>
          )}
        </div>

        {chargement && <div className="lab-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>}

        {!chargement && onglet !== "notifications" && (
          <div className="msg-conteneur">
            <div className="msg-liste">
              {messages.map((m) => (
                <button key={m.id}
                        className={`msg-item ${!m.lu && onglet === "reception" ? "non-lu" : ""} ${ouvert?.id === m.id ? "actif" : ""}`}
                        onClick={() => setOuvert(m)}>
                  <div className="msg-item-tete">
                    <strong>{onglet === "reception" ? m.expediteur_nom : m.destinataire_nom}</strong>
                    <span>{quand(m.date_envoi)}</span>
                  </div>
                  <div className="msg-item-objet">{m.objet || "(sans objet)"}</div>
                  <div className="msg-item-extrait">{m.contenu.slice(0, 90)}</div>
                </button>
              ))}
              {!messages.length && (
                <div className="lab-vide">
                  <i className="fa-regular fa-envelope"></i>
                  <p>{onglet === "reception" ? "Aucun message reçu." : "Aucun message envoyé."}</p>
                </div>
              )}
            </div>

            <div className="msg-lecture">
              {ouvert ? (
                <>
                  <div className="msg-lecture-tete">
                    <div>
                      <h2>{ouvert.objet || "(sans objet)"}</h2>
                      <p>
                        <strong>{ouvert.expediteur_nom}</strong> → {ouvert.destinataire_nom}
                        <span className="msg-date">{new Date(ouvert.date_envoi).toLocaleString("fr-FR")}</span>
                      </p>
                    </div>
                    <button className="msg-suppr" onClick={() => supprimer(ouvert.id)} title="Supprimer">
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                  <div className="msg-corps">{ouvert.contenu}</div>
                </>
              ) : (
                <div className="lab-vide">
                  <i className="fa-regular fa-envelope-open"></i>
                  <p>Sélectionnez un message.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!chargement && onglet === "notifications" && (
          <div className="notif-liste">
            {notifications.map((n) => (
              <button key={n.id} className={`notif-item ${n.lue ? "" : "non-lue"} type-${n.type}`}
                      onClick={() => lireNotification(n)}>
                <div className="notif-icone"><i className="fa-solid fa-bell"></i></div>
                <div className="notif-corps">
                  <div className="notif-tete">
                    <strong>{n.titre}</strong>
                    <span>{quand(n.date_creation)}</span>
                  </div>
                  <p>{n.message}</p>
                </div>
                {!n.lue && <span className="notif-point"></span>}
              </button>
            ))}
            {!notifications.length && (
              <div className="lab-vide">
                <i className="fa-regular fa-bell"></i>
                <p>Aucune notification.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {redaction && (
        <div className="lab-modale-fond" onClick={() => setRedaction(false)}>
          <div className="lab-modale" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>Nouveau message</h2>
              <button onClick={() => setRedaction(false)}><i className="fa-solid fa-xmark"></i></button>
            </header>
            <form onSubmit={envoyer}>
              <div className="champs-colonne">
                <label>Destinataire *
                  <select value={brouillon.destinataire} required
                          onChange={(e) => setBrouillon({ ...brouillon, destinataire: e.target.value })}>
                    <option value="">Choisir…</option>
                    {/* Envoi groupé : réservé à l'équipe du centre (contrôlé aussi côté serveur). */}
                    {authService.isCommercial() && (
                      <optgroup label="Envoi groupé">
                        <option value="groupe:tous">Tout le monde</option>
                        <option value="groupe:administration">Toute l'administration</option>
                        <option value="groupe:formateurs">Tous les formateurs</option>
                        <option value="groupe:etudiants">Tous les étudiants</option>
                      </optgroup>
                    )}
                    <optgroup label="Une personne">
                      {destinataires.map((d) => (
                        <option key={d.id} value={d.id}>{d.nom} — {d.role_display}</option>
                      ))}
                    </optgroup>
                  </select>
                  {authService.isApprenant() && (
                    <small className="aide-destinataires">
                      Vous pouvez écrire à vos formateurs, aux étudiants de vos sessions et à l'administration,
                      dès qu'ils ont un accès à la plateforme.
                    </small>
                  )}
                </label>
                <label>Objet
                  <input value={brouillon.objet}
                         onChange={(e) => setBrouillon({ ...brouillon, objet: e.target.value })} />
                </label>
                <label>Message *
                  <textarea rows="7" required value={brouillon.contenu}
                            onChange={(e) => setBrouillon({ ...brouillon, contenu: e.target.value })} />
                </label>
              </div>
              <footer>
                <button type="button" className="btn-lien" onClick={() => setRedaction(false)}>Annuler</button>
                <button type="submit" className="btn-primaire" disabled={envoi}>
                  <i className="fa-solid fa-paper-plane"></i> {envoi ? " Envoi…" : " Envoyer"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
